export type WsMessage =
  | { type: 'ready'; username: string }
  | { type: 'recording_saved'; recording_id: number; size_bytes: number;
      evicted: { count: number; freedBytes: number } }
  | { type: 'analysis_result'; recording_id: number; result: AnalysisResult }
  | { type: 'analysis_error';  recording_id: number; error: string }
  | { type: 'error'; error: string };

export interface RmsResult {
  block_size: number;
  sample_rate: number;
  num_blocks: number;
  sample_count: number;
  rms: number[];
}

export interface SpectrogramResult {
  block_size: number;
  sample_rate: number;
  num_blocks: number;
  num_bins: number;
  bin_hz_step: number;
  freq_min_hz: number;
  freq_max_hz: number;
  /** Block-major flat array: length === num_blocks * num_bins, integer dB. */
  data: number[];
}

export type UnmixMethod = 'block' | 'ssa' | 'ica' | 'nmf';

export type RpmDetectorName = 'yin' | 'cepstrum' | 'hss' | 'stft';

export interface RpmMethodResult {
  method: RpmDetectorName;
  rpm: number | null;
  fDom_hz: number | null;
  error: string | null;
  /** Multiplier ∈ {1, 2, 3, 6} that brings this detector in line with
   *  the consensus / gauge: 1 = clean lock; 2 = bank-alternation;
   *  3 = per-revolution; 6 = engine-cycle (misfire signature). */
  lock_factor: number | null;
  corrected_rpm: number | null;
}

export type RpmAgreement = 'all' | 'majority' | 'split' | 'none';
export type RpmCorrectionKind = 'gauge' | 'ladder' | 'none';

export type MicLocation = 'phone_cab' | 'phone_hood' | 'external_hood';

export const MIC_LOCATION_LABELS: Record<MicLocation, string> = {
  phone_cab: 'Phone in cab of vehicle',
  phone_hood: 'Phone under hood',
  external_hood: 'External mic under hood',
};

export interface ComponentsResult {
  sample_rate: number;
  /** Which decomposition method Octave used for this clip. */
  method: UnmixMethod;
  /** Number of components in this result. The UI renders comp1..compN
   *  based on this count rather than a hardcoded 5 — Octave is the source
   *  of truth. */
  num_components: number;
  display_length: number;
  singular_values: number[];
  /** num_components arrays, each display_length samples. */
  components: number[][];

  // ---- ICA-only fields (omitted for block / ssa) ----------------------
  rpm?: number;
  dominant_freq_hz?: number;
  num_cylinders?: number;
  num_strokes?: number;
  stroke_samples?: number;
  cycle_samples?: number;
  component_rms?: number[];
  /** Set when ICA failed to lock the firing-rate period; `components`
   *  will be empty and this carries the diagnostic message. */
  error?: string;
  /** Method-agnostic RPM detection diagnostic — set when the autocorr-
   *  based detector failed even though `components` may have computed
   *  fine (block / ssa). */
  rpm_error?: string;
  /** Per-method RPM estimates from the four detectors. Disagreement is
   *  itself a diagnostic (typically misfire). */
  rpm_methods?: RpmMethodResult[];
  rpm_agreement?: RpmAgreement;
  rpm_raw?: number;
  rpm_correction?: RpmCorrectionKind;
  rpm_diagnostic?: string;
  /** Per-cylinder slot-binned diagnostic. */
  cylinders?: CylinderHealthResult;
  accessories?: AccessoryResult[];
  vacuum_leak?: VacuumLeakResult;
  bearings?: BearingResult;
  ac_cycling?: AcCyclingResult;
  events?: EventTimelineResult;
  powertrain_state?: PowertrainStateResult;
}

export interface BearingPeak {
  freq_hz: number;
  amplitude: number;
  snr: number;
  shaft_ratio: number;
  candidate: string;
}

export interface BearingResult {
  band_lo_hz?: number;
  band_hi_hz?: number;
  noise_floor?: number;
  shaft_rpm?: number;
  shaft_freq_hz?: number;
  peaks: BearingPeak[];
  error?: string;
}

export interface AcTransition {
  time_sec: number;
  kind: 'engage' | 'disengage';
}

export interface AcCyclingResult {
  expected_hz?: number;
  pct_running?: number;
  num_transitions?: number;
  transitions?: AcTransition[];
  health?: 'on' | 'off' | 'cycling' | 'short-cycling' | 'unknown';
  pattern?: string;
  error?: string;
}

export interface DetectedEvent {
  time_start_sec: number;
  time_end_sec: number;
  duration_sec: number;
  peak_rms: number;
  centroid_hz: number;
  bandwidth_hz: number;
  kind: 'click' | 'tonal-burst' | 'broadband-burst' | 'sustained';
}

export interface EventTimelineResult {
  background?: number;
  threshold?: number;
  events: DetectedEvent[];
  error?: string;
}

// ---- Trend samples (compact longitudinal summaries) -------------------

export interface TrendAccessory {
  name: string;
  snr: number | null;
  peak_hz: number | null;
  deviation_hz: number | null;
  health: string | null;
}

export interface TrendCylinderAlignment {
  rotation: number;
  confidence: number;
  bootstrap: boolean;
  template_count: number;
}

export interface TrendSample {
  recording_id: number;
  recorded_at: number;
  duration_sec: number;
  rpm: number | null;
  gauge_rpm: number | null;
  rpm_agreement: string | null;
  smoothness_score: number | null;
  exhaust_leak_score: number | null;
  vacuum_leak_score: number | null;
  global_health: string | null;
  num_cylinders: number | null;
  cylinder_energies: number[] | null;
  cylinder_variances: number[] | null;
  cylinder_missed: number[] | null;
  cylinder_decay: (number | null)[] | null;
  cylinder_knock: number[] | null;
  cylinder_phase: number[] | null;
  cylinder_alignment: TrendCylinderAlignment | null;
  cylinder_confidences: number[] | null;
  cylinder_confidences_by_cyl: number[] | null;
  cylinder_energies_by_cyl: number[] | null;
  cylinder_variances_by_cyl: number[] | null;
  cylinder_missed_by_cyl: number[] | null;
  cylinder_decay_by_cyl: (number | null)[] | null;
  cylinder_knock_by_cyl: number[] | null;
  cylinder_phase_by_cyl: number[] | null;
  accessories: TrendAccessory[] | null;
  bearing_peak_count: number | null;
  max_bearing_snr: number | null;
  ac_health: string | null;
  ac_pct_running: number | null;
  num_events: number | null;
  num_click_events: number | null;
  num_tonal_events: number | null;
  num_broadband_events: number | null;
  num_sustained_events: number | null;
}

export interface VehicleMetricBaseline {
  metric_key: string;
  n: number;
  mean: number;
  std: number;
  warn_low: number;
  warn_high: number;
}

export interface TrendsResponse {
  vehicle_id: number;
  samples: TrendSample[];
  metric_stats?: VehicleMetricBaseline[];
}

export interface AccessoryResult {
  name: string;
  pulley_ratio: number;
  expected_hz: number;
  peak_hz: number | null;
  peak_amplitude: number | null;
  median_in_band: number | null;
  snr: number | null;
  present: boolean;
  deviation_hz: number | null;
  health: 'green' | 'yellow' | 'absent' | 'unknown';
}

export interface VacuumLeakResult {
  band_lo_hz?: number;
  band_hi_hz?: number;
  total_energy?: number;
  comb_energy?: number;
  residual_energy?: number;
  score?: number;
  health?: 'green' | 'yellow' | 'red';
  error?: string;
}

export interface PerCylinderMetrics {
  index: number;
  slot_index: number;
  health: 'green' | 'yellow' | 'red';
  energy: number;
  energy_pct_of_cohort: number;
  energy_per_cycle: number[];
  variance_pct: number;
  missed_count: number;
  cycles_total: number;
  phase_offset_deg: number;
  high_freq_ratio: number;
  spectral_centroid_hz: number;
  spectrum_thumb: number[];
  knock_energy: number;
  knock_severity: number;
  decay_ratio: number | null;
  confidence: number;
  vs_cohort_t?: number;
  vs_cohort_p?: number;
  vs_cohort_d?: number;
  flags: string[];
  /** Steps N–R Layer-2 SVM output: predicted persistent template
   *  slot, P(predicted), and full per-class distribution. Absent
   *  until the per-vehicle classifier is trained. */
  classifier_template_slot?: number;
  classifier_probability?: number;
  classifier_probabilities?: number[];
}

export interface PowertrainStateResult {
  state: 'engine' | 'ev' | 'off' | 'unknown';
  confidence: number;
  harmonic_ratio: number;
  intensity_dbfs: number;
  reason?: string;
}

export interface CycleFingerprintAlignment {
  rotation: number;
  costs: number[];
  confidence: number;
  bootstrap: boolean;
  template_count: number;
  skipped?: string;
}

export interface CylinderHealthResult {
  num_cylinders: number;
  cycle_samples?: number;
  cycles_analyzed: number;
  anchor_offset?: number;
  firing_order?: number[];
  cohort_mean_energy?: number;
  global_health?: 'green' | 'yellow' | 'red';
  smoothness_score?: number;
  exhaust_leak_score?: number;
  /** Layer-1 alignment result. The full fingerprint vector emitted by
   *  Octave is intentionally NOT typed here — clients only consume the
   *  alignment metadata, not the raw vector. */
  cycle_fingerprint_alignment?: CycleFingerprintAlignment;
  cylinders: PerCylinderMetrics[];
  error?: string;
}

export interface AnalysisResult {
  rms: RmsResult;
  spectrogram: SpectrogramResult;
  components: ComponentsResult;
}

export interface MetaPayload {
  sample_rate: number;
  channels: number;
  duration_sec: number;
  vehicle_id: number;
  unmix_method: UnmixMethod;
  trip_start_km: number;
  trip_purpose: string;
  mic_location: MicLocation;
  num_cylinders: number;
  num_strokes: number;
  num_valves_per_cylinder: number;
  /** Best-effort label of the actual MediaStream track at recording time
   *  (e.g. "Built-in Microphone", "Car USB Audio"). Populated from
   *  MediaStreamTrack.label, which the OS controls. Empty when the
   *  browser hasn't surfaced it yet. */
  mic_device_label?: string;
}

type Listener = (msg: WsMessage) => void;
type CloseListener = (info: { code: number; intentional: boolean }) => void;

export class WsClient {
  private socket: WebSocket | null = null;
  private listeners: Listener[] = [];
  private closeListeners: CloseListener[] = [];
  private intentionalClose = false;

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${location.host}/ws`;
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      const onOpen = () => { cleanup(); resolve(); };
      const onErr = (e: Event) => { cleanup(); reject(new Error('ws connect failed: ' + (e as ErrorEvent).message)); };
      const cleanup = () => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onErr);
      };
      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onErr);
      ws.addEventListener('message', (e) => {
        if (typeof e.data !== 'string') return;
        try {
          const msg = JSON.parse(e.data) as WsMessage;
          for (const l of this.listeners) l(msg);
        } catch { /* ignore non-JSON */ }
      });
      ws.addEventListener('close', (e) => {
        const intentional = this.intentionalClose;
        this.intentionalClose = false;
        for (const l of this.closeListeners) l({ code: e.code, intentional });
      });
      this.socket = ws;
    });
  }

  on(fn: Listener): () => void {
    this.listeners.push(fn);
    return () => { this.listeners = this.listeners.filter((x) => x !== fn); };
  }

  onClose(fn: CloseListener): () => void {
    this.closeListeners.push(fn);
    return () => { this.closeListeners = this.closeListeners.filter((x) => x !== fn); };
  }

  isOpen(): boolean { return this.socket?.readyState === WebSocket.OPEN; }

  /** Returns the underlying readyState, or CLOSED if no socket exists. */
  get readyState(): number {
    return this.socket?.readyState ?? WebSocket.CLOSED;
  }

  sendMeta(meta: MetaPayload) {
    this.socket?.send(JSON.stringify({ type: 'meta', ...meta }));
  }

  sendBinary(bytes: Uint8Array) {
    this.socket?.send(bytes);
  }

  /** Close intentionally — close listeners will see `intentional: true`. */
  close() {
    this.intentionalClose = true;
    this.socket?.close();
    this.socket = null;
  }
}
